import { IAuthInfo, getLoginServerFromLocalStorage } from '../context/AuthContext';
import { SS_KEY } from '../constants'

// see: https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
export function parseJwt(token: string) {
  let result = null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    result = JSON.parse(jsonPayload);
  } catch (err) {
    console.error(`Fail to parse jwt token ${token}`, err)
  }
  return result;
};

export function getOAuthPortalAddress(action: "signup" | "login" | "logout"): string {
  // assemble redirect url
  const path = action === "logout"
    ? "logout"
    : action === "login"
      ? "oauth2/authorize"
      : "signup";
  const searchParams = new URLSearchParams({
    client_id: process.env.REACT_APP_CLIENT_ID || "",
  })
  if (action === "logout") {
    searchParams.set("logout_uri", window.location.origin)
  } else {
    searchParams.set("response_type", "code")
    searchParams.set("redirect_uri", window.location.origin)
  }
  const loginServer = getLoginServerFromLocalStorage();
  const url = `${loginServer}/${path}?${searchParams.toString()}`
  return url;
}

export function gotoOAuthPortal(action: "signup" | "login" | "logout") {
  // udpate redirect back path before login
  const { pathname, search, hash } = window.location;
  if (action === "login" || action === "signup") {
    sessionStorage.setItem(SS_KEY.PATH_BEFORE_SIGNIN, pathname + search + hash)
  }
  // redirect
  const url = getOAuthPortalAddress(action);
  window.location.replace(url)
}

export function getSwitchAccountUrl(): string {
  const loginServer = getLoginServerFromLocalStorage();
  const authorizeUrl = getOAuthPortalAddress("login");
  const searchParams = new URLSearchParams({
    client_id: process.env.REACT_APP_CLIENT_ID || "",
    logout_uri: authorizeUrl,
  });
  return `${loginServer}/logout?${searchParams.toString()}`;
}


/**
 * Renew the id and access tokens with the refresh token.
 *
 * Access and id tokens last a day; the refresh token lasts 30. Without this a
 * user returning after a day is signed out and made to log in again, which
 * defeats the 30-day session.
 *
 * Uses the Cognito IDP API rather than the hosted UI's /oauth2/token, which
 * only accepts grant_type=authorization_code on this app client. InitiateAuth
 * needs no signing, so this stays a plain fetch with no SDK dependency.
 *
 * Cognito does not rotate refresh tokens by default, so the response carries no
 * new one and the existing token is kept.
 */
export async function refreshAuthInfoAsync(info: IAuthInfo): Promise<IAuthInfo> {
  if (!info.refresh_token) throw new Error("no refresh_token");
  const region = process.env.REACT_APP_COGNITO_REGION || "";
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
    },
    body: JSON.stringify({
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: process.env.REACT_APP_CLIENT_ID || "",
      AuthParameters: { REFRESH_TOKEN: info.refresh_token },
    }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`refresh failed (${res.status}): ${body.slice(0, 120)}`);
  }
  const { AuthenticationResult } = await res.json();
  const id_token = AuthenticationResult?.IdToken;
  const access_token = AuthenticationResult?.AccessToken;
  if (!id_token) throw new Error("refresh returned no IdToken");
  return {
    email: parseJwt(id_token)?.email || info.email,
    id_token,
    access_token,
    refresh_token: info.refresh_token,
  };
}

export async function fetchAuthInfoAsync(code: string): Promise<IAuthInfo> {
  const loginServer = getLoginServerFromLocalStorage();
  const url = `${loginServer}/oauth2/token`;
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      redirect_uri: window.location.origin,
      code: code,
      client_id: process.env.REACT_APP_CLIENT_ID || "",
    }),
  };

  const res = await fetch(url, options);

  if (res.ok) {
    // refresh_token must be kept: the portal SPA renews sessions with it, and
    // without it every session ends at expiry with a full trip through Cognito.
    const { id_token, access_token, refresh_token } = await res.json();
    const email = parseJwt(id_token)?.email;
    return { email, access_token, id_token, refresh_token }
  }
  throw new Error(res.statusText)
}
